import { useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { saveBlobResponse } from '../utils/download';
import { todayISO } from '../utils/dates';

import DateInput from './DateInput';
import { formatDate } from '../utils/dateFormat';
const STATUS_BADGE = { NEW: 'success', LINK: 'info', DUPLICATE: 'secondary', INVALID: 'danger' };
const STATUS_LABEL = { NEW: 'New item', LINK: 'From Material Master', DUPLICATE: 'Already there', INVALID: 'Needs fixing' };
const MAX_ROWS_SHOWN = 300;

const errorText = async (error, fallback) => {
  let data = error.response?.data;
  if (data instanceof Blob) data = await data.text();
  return typeof data === 'string' && data ? data : fallback;
};

/**
 * Org Admin > Import / Export: items (Materials) from a Tally masters export or a CSV, linked from the Global Material Master
 * where the name matches, otherwise added as the organization's own. Preview first; nothing is saved until confirmed.
 */
function ItemImport() {
  const fileInput = useRef(null);
  const [format, setFormat] = useState('TALLY');
  const [file, setFile] = useState(null);
  const [itemTypes, setItemTypes] = useState([]);
  const [itemTypeId, setItemTypeId] = useState('');
  const [asProducts, setAsProducts] = useState(false);
  const [openingDate, setOpeningDate] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios.get('/api/organization-item-types')
      .then((r) => {
        const list = (Array.isArray(r.data) ? r.data : []).filter((t) => t.isActive !== false);
        setItemTypes(list);
        const fallback = list.find((t) => (t.name || '').toLowerCase() === 'default') || (list.length === 1 ? list[0] : null);
        if (fallback) setItemTypeId(String(fallback.id));
      })
      .catch(() => {});
  }, []);

  const reset = () => { setPreview(null); setDone(null); };

  const send = async (action) => {
    const body = new FormData();
    body.append('file', file);
    body.append('format', format);
    if (itemTypeId) body.append('itemTypeId', itemTypeId);
    body.append('asProducts', String(asProducts));
    if (openingDate) body.append('openingDate', openingDate);
    const response = await axios.post(`/api/data-import/items/${action}`, body);
    return response.data;
  };

  const handlePreview = async () => {
    if (!file) { toast.error('Choose a file first'); return; }
    try {
      setBusy(true);
      setDone(null);
      setPreview(await send('preview'));
    } catch (error) {
      setPreview(null);
      toast.error(await errorText(error, 'Failed to read the file'));
    } finally {
      setBusy(false);
    }
  };

  const handleCommit = async () => {
    try {
      setBusy(true);
      const result = await send('commit');
      setDone(result);
      setPreview(null);
      setFile(null);
      if (fileInput.current) fileInput.current.value = '';
      const added = result.newCount + result.linkCount;
      toast.success(`Added ${added} item${added === 1 ? '' : 's'} to your Materials${result.openingCount > 0 ? `, with opening stock for ${result.openingCount}` : ''}`);
    } catch (error) {
      toast.error(await errorText(error, 'Import failed - nothing was imported'));
    } finally {
      setBusy(false);
    }
  };

  const template = async () => {
    try {
      saveBlobResponse(await axios.get('/api/data-import/templates/items.csv', { responseType: 'blob' }), 'items-template.csv');
    } catch (error) {
      toast.error('Failed to download the template');
    }
  };

  const importable = preview ? preview.newCount + preview.linkCount : 0;
  // Opening stock needs a date it is as at; the organization's own if it has one, else the one chosen here
  const needsDate = Boolean(preview?.needsOpeningDate) && !openingDate;
  const shownRows = preview ? preview.rows.slice(0, MAX_ROWS_SHOWN) : [];

  return (
    <Card>
      <Card.Header><h5 className="mb-0">Import items</h5></Card.Header>
      <Card.Body>
        <p className="text-muted">
          Bring your items (raw materials, finished goods, by-products) into Materials from a Tally masters export or a CSV, with
          their unit, HSN code, SKU and GST rate. An item already in the Global Material Master under the same name is used as it is; others are
          added as your own. Items already in your Materials are skipped. You see a preview first.
        </p>
        <Row className="g-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label>File type</Form.Label>
              <Form.Select value={format} onChange={(e) => { setFormat(e.target.value); reset(); }}>
                <option value="TALLY">Tally masters export (XML)</option>
                <option value="ITEMS_CSV">Items (CSV)</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={8}>
            <Form.Group>
              <Form.Label>File</Form.Label>
              <Form.Control ref={fileInput} type="file" accept={format === 'TALLY' ? '.xml' : '.csv'}
                onChange={(e) => { setFile(e.target.files[0] || null); reset(); }} />
            </Form.Group>
          </Col>
        </Row>
        {format === 'ITEMS_CSV' && (
          <p className="small mt-2 mb-0">
            Columns: Name, Unit, HSN, SKU, Category, Sale Price, GST Rate, and for stock you already hold: Opening Qty, Cost Per Unit,
            Warehouse (blank if you have only one place), Lot, Expiry Date (2027-06-30; kept only if your organization keeps expiry dates). Only Name is needed.{' '}
            <Button variant="link" size="sm" className="p-0 align-baseline" onClick={template}>Download the items template</Button>
          </p>
        )}
        <Row className="g-3 mt-1">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Category when the file doesn&apos;t say</Form.Label>
              <Form.Select value={itemTypeId} onChange={(e) => { setItemTypeId(e.target.value); reset(); }}>
                <option value="">-- Choose --</option>
                {itemTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </Form.Select>
              <Form.Text className="text-muted">
                {format === 'TALLY' ? 'A Tally stock group with the same name as a category is used instead.' : 'Used when the Category column is empty or unknown.'}
              </Form.Text>
            </Form.Group>
          </Col>
          <Col md={8} className="d-flex align-items-start pt-md-4">
            <Form.Check type="checkbox" id="imp-items-products"
              label="Also add them as products you sell (rows with a sale price always are)"
              checked={asProducts} onChange={(e) => { setAsProducts(e.target.checked); reset(); }} />
          </Col>
        </Row>

        <div className="mt-3">
          <Button variant="outline-primary" onClick={handlePreview} disabled={busy || !file}>
            {busy && !preview ? <Spinner animation="border" size="sm" /> : 'Preview'}
          </Button>
        </div>

        {done && (
          <Alert variant="success" className="mt-3 mb-0">
            Added {done.newCount + done.linkCount} item{done.newCount + done.linkCount === 1 ? '' : 's'} to your Materials
            {done.productCount > 0 && `, ${done.productCount} also as products`}.
            {done.openingCount > 0 && ` Opening stock was booked for ${done.openingCount} (worth ${Number(done.openingValue).toFixed(2)}, as at ${formatDate(done.openingDate)}).`}
            {done.duplicateCount > 0 && ` ${done.duplicateCount} already there ${done.duplicateCount === 1 ? 'was' : 'were'} skipped.`}
            {done.invalidCount > 0 && ` ${done.invalidCount} need${done.invalidCount === 1 ? 's' : ''} fixing and ${done.invalidCount === 1 ? 'was' : 'were'} not imported.`}
          </Alert>
        )}

        {preview && (
          <div className="mt-4">
            <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
              <Badge bg="success">{preview.newCount} new</Badge>
              <Badge bg="info">{preview.linkCount} from the Material Master</Badge>
              <Badge bg="secondary">{preview.duplicateCount} already there</Badge>
              <Badge bg="danger">{preview.invalidCount} need fixing</Badge>
              {preview.productCount > 0 && <Badge bg="primary">{preview.productCount} as products</Badge>}
              {preview.openingCount > 0 && <Badge bg="dark">{preview.openingCount} with opening stock, worth {Number(preview.openingValue).toFixed(2)}</Badge>}
              <span className="text-muted small">{preview.total} rows read</span>
              <Button variant="primary" className="ms-auto" onClick={handleCommit} disabled={busy || importable === 0 || needsDate}>
                {busy ? 'Importing...' : `Import ${importable} item${importable === 1 ? '' : 's'}`}
              </Button>
            </div>
            {preview.openingCount > 0 && (
              <div className="mb-3">
                {preview.openingDate ? (
                  <p className="small mb-0">
                    Opening stock is counted as at <strong>{formatDate(preview.openingDate)}</strong>, the date your opening balances are as at.
                  </p>
                ) : (
                  <Form.Group style={{ maxWidth: 360 }}>
                    <Form.Label>Opening stock is as at</Form.Label>
                    <DateInput max={todayISO()} value={openingDate} onChange={(e) => setOpeningDate(e.target.value)} />
                    <Form.Text className="text-muted">
                      The day your stock count is as of. It is set once for all your opening balances and can&apos;t be changed after any are entered.
                    </Form.Text>
                  </Form.Group>
                )}
              </div>
            )}
            <Table striped bordered hover responsive size="sm">
              <thead>
                <tr><th>Line</th><th>Name</th><th>Unit</th><th>HSN</th><th>GST</th><th>Category</th>{preview.openingCount > 0 && <th>Opening stock</th>}<th>Result</th><th>Details</th></tr>
              </thead>
              <tbody>
                {shownRows.map((row, i) => (
                  <tr key={`${row.line}-${i}`}>
                    <td>{row.line}</td>
                    <td>{row.name}{row.product && <Badge bg="primary" className="ms-2">product</Badge>}</td>
                    <td>{row.unit || '-'}</td>
                    <td>{row.hsn || '-'}</td>
                    <td>{row.gstRate != null ? `${Number(row.gstRate)}%` : '-'}</td>
                    <td>{row.itemType || '-'}</td>
                    {preview.openingCount > 0 && <td className="small">{row.opening || '-'}</td>}
                    <td><Badge bg={STATUS_BADGE[row.status] || 'secondary'}>{STATUS_LABEL[row.status] || row.status}</Badge></td>
                    <td className="small">
                      {row.message}
                      {(row.warnings || []).map((w) => <div key={w} className="text-warning">{w}</div>)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {preview.rows.length > MAX_ROWS_SHOWN && (
              <p className="text-muted small">Showing the first {MAX_ROWS_SHOWN} of {preview.rows.length} rows; the import covers all of them.</p>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

export default ItemImport;
